import React, { useEffect, useState } from 'react';
import { PenTool } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../stores';
import { FamlaServices } from '../../services';
import { Routes } from '../../routes';
import { navigateToInterviewWithFallback } from '../../utils/smartNavigation';
import { navigateToLatestManualMapping } from '../../utils/mappingAccess';
import { formatProcessCardUpdatedAt, getProcessModifiedTimestamp } from '../../utils/formatDate';
import type iProcess from '../../components/type/Process';
import Dropdown from '../../components/Dropdown';
import ProcessMethodBadge from '../../components/process/ProcessMethodBadge';
import { useAllInterviewsCompleted } from '../../hooks/useAllInterviewsCompleted';
import { computeHomeProcessDisplayStatus, type HomeProcessDisplayStatus } from '../../utils/processHomeStatus';
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../services/api/uiButtonStatsService';
import { getProcessCardDetailLine, isManualProcess } from '../../utils/processTypeUtils';

export type HomeProcessStatus = HomeProcessDisplayStatus;

interface HomeProcessRowProps {
    process: iProcess;
    ownerLabel: string;
    hasNotif: boolean;
    isOwnerMe: boolean;
    deleteProcess?: () => void;
    setProcess?: (data: any) => void;
    /** Si défini, ouverture suppression sans lien `data-bs-toggle` (évite modale au chargement). */
    onRequestDelete?: () => void;
}

const HomeProcessRow: React.FC<HomeProcessRowProps> = ({
    process,
    ownerLabel,
    hasNotif,
    isOwnerMe,
    deleteProcess,
    setProcess,
    onRequestDelete,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const users = useAppSelector((state) => state.auth);
    const companyRole = useAppSelector((state) => state.role.role);
    /** Porteur ou co-propriétaire (`GET /process/my/role`). */
    const [processManageRights, setProcessManageRights] = useState<boolean | null>(null);
    const [interviewId, setInterviewId] = useState<string | undefined>();
    const [isNavigating, setIsNavigating] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await FamlaServices(
                'api_host',
                `/process/my/role/${process._id}`,
                'GET',
                '',
                users.access_token,
            );
            if (cancelled) return;
            const ok = res.status === 200 && res.body?.data?.is_process_manager === true;
            setProcessManageRights(ok);
        })();
        return () => {
            cancelled = true;
        };
    }, [process._id, users.access_token]);

    /** Menu ⋯ : WORKER seulement si gestionnaire du process (API). */
    const dropdownAllowed =
        companyRole !== 'WORKER' || isOwnerMe || processManageRights === true;
    const canRunOwnerMenus =
        processManageRights === true || (processManageRights === null && isOwnerMe);

    useEffect(() => {
        const getInterview = async () => {
            const data = await FamlaServices('api_host', `/interview/process/${process._id}`, 'GET', '', users.access_token);
            if (data.status === 200 && data.body?.data?._id) {
                setInterviewId(data.body.data._id);
            }
        };
        if (process._id) {
            getInterview();
        }
    }, [process._id, users.access_token]);

    const allInterviewsCompleted = useAllInterviewsCompleted(process._id);
    const status: HomeProcessStatus = computeHomeProcessDisplayStatus(hasNotif, allInterviewsCompleted);

    const pillLabel =
        status === 'new'
            ? t('views.processView.homeStatusNew')
            : status === 'completed'
              ? t('views.processView.homeStatusCompleted')
              : t('views.processView.homeStatusOngoing');

    const pillClass =
        status === 'new' ? 'home-pill home-pill-new' : status === 'completed' ? 'home-pill home-pill-done' : 'home-pill home-pill-ongoing';

    const updatedRaw = getProcessModifiedTimestamp(process as unknown as Record<string, unknown>);
    const updatedLabel = updatedRaw !== undefined ? formatProcessCardUpdatedAt(updatedRaw) : '';

    const processDetailLine = getProcessCardDetailLine(process, t);
    const isManual = isManualProcess(process.type);

    const handleClick = async () => {
        if (!process._id || isNavigating) return;
        await trackUiButtonEventAsync(UI_BUTTON_EVENTS.home.recentOpenProcess);
        setIsNavigating(true);
        try {
            if (isManual) {
                await navigateToLatestManualMapping({
                    processId: process._id,
                    accessToken: users.access_token,
                    navigate,
                });
                return;
            }

            const success = await navigateToInterviewWithFallback(process._id, users.access_token, navigate, {
                onSwitchStart: () => setIsSwitching(true),
                onSwitchComplete: () => setIsSwitching(false),
                onError: () => {
                    if (interviewId) navigate(Routes.PROCESS_INTERVIEW + interviewId);
                },
                updateSelection: setInterviewId,
            });
            if (!success && interviewId) {
                navigate(Routes.PROCESS_INTERVIEW + interviewId);
            }
        } finally {
            setIsNavigating(false);
            setIsSwitching(false);
        }
    };

    return (
        <>
            <div
                role="button"
                tabIndex={isNavigating ? -1 : 0}
                className="home-process-row home-process-row--processmap"
                aria-disabled={isNavigating}
                aria-busy={isNavigating}
                onClick={() => {
                    if (!isNavigating) void handleClick();
                }}
                onKeyDown={(e) => {
                    if (isNavigating) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void handleClick();
                    }
                }}
            >
            <div className={`home-p-icon${isManual ? ' home-p-icon--manual' : ''}`} aria-hidden>
                {isManual ? (
                    <PenTool size={18} strokeWidth={1.8} />
                ) : (
                    <svg viewBox="0 0 16 16" fill="none">
                        <path
                            d="M2 4.5C2 3.4 2.9 2.5 4 2.5H12C13.1 2.5 14 3.4 14 4.5V5.5C14 6.6 13.1 7.5 12 7.5H4C2.9 7.5 2 6.6 2 5.5V4.5Z"
                            stroke="currentColor"
                            strokeWidth="1.2"
                        />
                        <path
                            d="M2 10.5C2 9.4 2.9 8.5 4 8.5H8C9.1 8.5 10 9.4 10 10.5V11.5C10 12.6 9.1 13.5 8 13.5H4C2.9 13.5 2 12.6 2 11.5V10.5Z"
                            stroke="currentColor"
                            strokeWidth="1.2"
                        />
                    </svg>
                )}
            </div>
            <div className="home-p-info">
                <div className="home-p-name-row">
                    <span className="home-p-name">{process.title}</span>
                    <ProcessMethodBadge processType={process.type} />
                </div>
                <div className="home-p-meta home-p-meta--processmap">
                    <div className="home-p-meta-main">
                        <span className={isOwnerMe ? 'home-owner-me' : 'home-owner-other'}>{ownerLabel}</span>
                        <span className="home-mdot" aria-hidden />
                        <span className="home-dc-objective" title={processDetailLine}>
                            {processDetailLine}
                        </span>
                        <span className="home-mdot" aria-hidden />
                        <span className={`${pillClass} home-dc-pill-shrink`}>{pillLabel}</span>
                        {isSwitching && <span className="text-muted small">…</span>}
                    </div>
                    {updatedLabel && (
                        <span
                            className="home-p-date"
                            title={`${t('views.processByGroupView.lastModifiedDate')}: ${updatedLabel}`}
                        >
                            {updatedLabel}
                        </span>
                    )}
                </div>
            </div>
            <div className="home-p-menu" onClick={(e) => e.stopPropagation()}>
                {dropdownAllowed ? (
                    <Dropdown
                        dropName=""
                        dropdownType="dropdown dropstart"
                        iconClassName="fa fa-ellipsis-h"
                        buttonDropClassName="btn btn-secondary bg-transparent border-0 p-0"
                        styleButton={{}}
                        onMenuButtonClick={() => {
                            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.home.recentRowMenu);
                        }}
                        addProcessOwner={
                            canRunOwnerMenus ? t('views.processView.addProcessOwner') : undefined
                        }
                        addProcessOwnerDataBs={
                            canRunOwnerMenus ? `#addProcessOwner${process._id}` : undefined
                        }
                        action3={`${t('words.organize')}`}
                        theRouteOfAction4=""
                        deleteProcess={deleteProcess}
                        action1=""
                        theRouteOfAction5=""
                        dataBsTarget={`#asignProcessToFolder${process._id}`}
                        routeFaq=""
                        edit={canRunOwnerMenus ? `${t('words.rename')}` : ''}
                        dataBsTargetEdit={`#edit${process._id}`}
                        publish={canRunOwnerMenus ? `${t('navigation.markAsFinal')}` : ''}
                        dataBsTargetPublish={`#publish${process._id}`}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        action7={canRunOwnerMenus ? t('words.delete') : ''}
                        action7OnClick={canRunOwnerMenus ? onRequestDelete : undefined}
                        dataBsTargetAction7={
                            canRunOwnerMenus ? (onRequestDelete ? undefined : `#generic${process._id}`) : ''
                        }
                    />
                ) : (
                    <span
                        className="text-secondary d-inline-block"
                        style={{ fontSize: 16, letterSpacing: 1, minWidth: 20 }}
                        aria-hidden
                    >
                        &nbsp;
                    </span>
                )}
            </div>
            <svg className="home-p-arr" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            </div>
        </>
    );
};

export default HomeProcessRow;
